/**
 * Resolve GraphQL HTTP / WS bases from REACT_APP_* env.
 *
 * Accepts anything people put in `.env`:
 *   https://ig.jedhub.online
 *   ig.jedhub.online
 *   http://127.0.0.1:5101
 *   127.0.0.1:5101
 *   localhost:5101
 *   auto / empty  → same origin as the page (prod / desktop / rsbuild proxy)
 */

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function stripTrailingApi(value: string): string {
  return value.replace(/\/api\/?$/i, "");
}

function pageOrigin(fallback?: string | null): string {
  if (fallback != null && String(fallback).trim()) {
    return stripTrailingSlash(String(fallback).trim());
  }
  // globalThis works in both the browser and the Node test harness (window is
  // not declared in the Node program that imports this helper for its pure fns).
  const g = globalThis as { location?: { origin?: string } };
  if (g?.location?.origin) {
    return stripTrailingSlash(g.location.origin);
  }
  return "";
}

function looksLocalHost(hostPort: string): boolean {
  return /^(localhost|127\.0\.0\.1|\[::1\]|0\.0\.0\.0)(:\d+)?$/i.test(
    hostPort.split("/")[0] || "",
  );
}

/** Normalize a configured HTTP API origin (no trailing slash, no `/api`). */
export function normalizeHttpApiBase(
  raw: string | null | undefined,
  pageOriginOverride?: string | null,
): string {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed || /^auto$/i.test(trimmed) || trimmed === "/") {
    return pageOrigin(pageOriginOverride);
  }

  let value = stripTrailingApi(stripTrailingSlash(trimmed));
  if (value.startsWith("//")) {
    value = `https:${value}`;
  }

  if (!/^https?:\/\//i.test(value) && !/^wss?:\/\//i.test(value)) {
    const host = value.replace(/^\/+/, "");
    const scheme = looksLocalHost(host) ? "http" : "https";
    value = `${scheme}://${host}`;
  }

  // Mistaken WS URL in the HTTP slot → coerce.
  value = value.replace(/^ws:/i, "http:").replace(/^wss:/i, "https:");
  return stripTrailingApi(stripTrailingSlash(value));
}

export function httpToWsBase(httpBase: string): string {
  const base = stripTrailingSlash(String(httpBase || ""));
  if (!base) return "";
  if (/^https:/i.test(base)) return base.replace(/^https:/i, "wss:");
  if (/^http:/i.test(base)) return base.replace(/^http:/i, "ws:");
  if (/^wss?:/i.test(base)) return base;
  return base;
}

/** Normalize WS origin; empty/auto derives from the HTTP base. */
export function normalizeWsApiBase(
  raw: string | null | undefined,
  httpBase: string,
): string {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed || /^auto$/i.test(trimmed) || trimmed === "/") {
    return httpToWsBase(httpBase);
  }

  let value = stripTrailingApi(stripTrailingSlash(trimmed));
  if (value.startsWith("//")) {
    value = `wss:${value}`;
  }

  if (!/^wss?:\/\//i.test(value) && !/^https?:\/\//i.test(value)) {
    const host = value.replace(/^\/+/, "");
    const scheme = looksLocalHost(host) ? "ws" : "wss";
    value = `${scheme}://${host}`;
  }

  value = value.replace(/^http:/i, "ws:").replace(/^https:/i, "wss:");
  return stripTrailingApi(stripTrailingSlash(value));
}

/** Runtime HTTP API origin for axios / media fetches. */
export function getApiBase(): string {
  return normalizeHttpApiBase(process.env.REACT_APP_API_URL);
}

/** Runtime WS origin for graphql-ws (`…/api`). */
export function getWsApiBase(): string {
  return normalizeWsApiBase(process.env.REACT_APP_WS_API_URL, getApiBase());
}

export function apiHttpUrl(path = "/api"): string {
  const base = getApiBase();
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

export function apiWsUrl(path = "/api"): string {
  const base = getWsApiBase();
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}
